// Monthly cost estimates in USD for common cloud SKUs.
//
// These are BALLPARK numbers, not spec-accurate billing. They assume
// "small production" defaults (730h/mo, modest storage, typical traffic)
// so architects can see order-of-magnitude cost as they design — not a
// finance-grade invoice forecast. Pricing fluctuates; revisit quarterly.
//
// Keyed by the same node-type strings the icon library uses (aws.ec2,
// gcp.cloud_sql, azure.vm, …). Unknown node types get $0 with a "no
// estimate" flag so the UI can distinguish "free" from "unpriced".
//
// When you do a refresh, update LAST_REVIEWED to today's date in
// YYYY-MM-DD form. `npm run cost:refresh` reads it to tell you whether
// the catalog is stale (>90 days). Don't bump the date without actually
// spot-checking — the freshness signal is only useful if it's honest.

export const LAST_REVIEWED = '2026-03-01'; // YYYY-MM-DD, UTC
export const REFRESH_INTERVAL_DAYS = 90;

export interface CostEntry {
  /** USD / month. Rounded to whole dollars — precision here is false comfort. */
  usd: number;
  /** One-line assumption behind the number. Shown in the tooltip. */
  basis: string;
  /** Coarse family for breakdown grouping. */
  family: 'compute' | 'storage' | 'database' | 'network' | 'security' | 'ops' | 'ai' | 'messaging' | 'analytics' | 'cdn';
}

const TABLE: Record<string, CostEntry> = {
  // ── AWS ────────────────────────────────────────────────────────
  'aws.ec2':               { usd: 30,  basis: 't3.small, 730h/mo',               family: 'compute' },
  'aws.ecs':               { usd: 50,  basis: '2×small Fargate tasks',            family: 'compute' },
  'aws.eks':               { usd: 75,  basis: 'cluster fee + 2 nodes',            family: 'compute' },
  'aws.fargate':           { usd: 50,  basis: '2×small tasks continuous',         family: 'compute' },
  'aws.lambda':            { usd: 5,   basis: '1M invocations, 200ms avg',        family: 'compute' },
  'aws.app_runner':        { usd: 25,  basis: '0.25 vCPU, always-on',             family: 'compute' },
  'aws.elastic_beanstalk': { usd: 30,  basis: 'wraps t3.small',                   family: 'compute' },

  'aws.alb':               { usd: 20,  basis: 'ALB, low traffic',                 family: 'network' },
  'aws.nlb':               { usd: 20,  basis: 'NLB, low traffic',                 family: 'network' },
  'aws.elb':               { usd: 18,  basis: 'classic LB',                       family: 'network' },
  'aws.api_gateway':       { usd: 10,  basis: '1M REST calls',                    family: 'network' },
  'aws.nat_gateway':       { usd: 35,  basis: '1 AZ, minimal data',               family: 'network' },
  'aws.internet_gateway':  { usd: 0,   basis: 'free',                             family: 'network' },
  'aws.transit_gateway':   { usd: 40,  basis: '1 attachment',                     family: 'network' },
  'aws.direct_connect':    { usd: 200, basis: '1 Gbps port, shared',              family: 'network' },
  'aws.vpc':               { usd: 0,   basis: 'free',                             family: 'network' },
  'aws.subnet':            { usd: 0,   basis: 'free',                             family: 'network' },
  'aws.route53':           { usd: 1,   basis: '1 hosted zone',                    family: 'network' },
  'aws.cloudfront':        { usd: 15,  basis: '100GB egress',                     family: 'cdn' },

  'aws.s3':                { usd: 5,   basis: '100GB standard + mild requests',   family: 'storage' },
  'aws.efs':               { usd: 15,  basis: '50GB standard',                    family: 'storage' },
  'aws.backup':            { usd: 10,  basis: '100GB incremental',                family: 'storage' },
  'aws.ecr':               { usd: 1,   basis: '10GB images',                      family: 'storage' },

  'aws.rds':               { usd: 50,  basis: 'db.t3.small, Single-AZ',           family: 'database' },
  'aws.aurora':            { usd: 90,  basis: '2× db.r6g.large writer+reader',    family: 'database' },
  'aws.dynamodb':          { usd: 15,  basis: '25 RCU/WCU on-demand',             family: 'database' },
  'aws.elasticache':       { usd: 50,  basis: 'cache.t3.small Redis',             family: 'database' },
  'aws.redshift':          { usd: 180, basis: 'dc2.large single node',            family: 'analytics' },
  'aws.opensearch':        { usd: 90,  basis: 't3.small.search',                  family: 'analytics' },

  'aws.sqs':               { usd: 1,   basis: '1M messages',                      family: 'messaging' },
  'aws.sns':               { usd: 1,   basis: '1M notifications',                 family: 'messaging' },
  'aws.eventbridge':       { usd: 1,   basis: '1M events',                        family: 'messaging' },
  'aws.kinesis':           { usd: 15,  basis: '1 shard',                          family: 'messaging' },
  'aws.msk':               { usd: 220, basis: 'kafka.t3.small 3-broker cluster',  family: 'messaging' },
  'aws.step_functions':    { usd: 3,   basis: '10k state transitions',            family: 'messaging' },

  'aws.cloudwatch':        { usd: 10,  basis: 'logs + custom metrics',            family: 'ops' },
  'aws.cloudtrail':        { usd: 2,   basis: 'management events only',           family: 'ops' },
  'aws.config':            { usd: 5,   basis: 'configuration items',              family: 'ops' },
  'aws.systems_manager':   { usd: 0,   basis: 'free at small scale',              family: 'ops' },

  'aws.iam':               { usd: 0,   basis: 'free',                             family: 'security' },
  'aws.cognito':           { usd: 0,   basis: 'free below 50k MAU',               family: 'security' },
  'aws.secrets_manager':   { usd: 2,   basis: '5 secrets',                        family: 'security' },
  'aws.waf':               { usd: 10,  basis: '1 web ACL',                        family: 'security' },
  'aws.shield':            { usd: 0,   basis: 'Standard is free',                 family: 'security' },
  'aws.guardduty':         { usd: 15,  basis: 'single-account small org',         family: 'security' },

  'aws.sagemaker':         { usd: 70,  basis: 'ml.t3.medium notebook',            family: 'ai' },
  'aws.bedrock':           { usd: 30,  basis: 'on-demand, moderate usage',        family: 'ai' },

  // ── GCP ────────────────────────────────────────────────────────
  'gcp.compute_engine':    { usd: 25,  basis: 'e2-small, 730h',                   family: 'compute' },
  'gcp.gke':               { usd: 75,  basis: 'cluster + 2 nodes',                family: 'compute' },
  'gcp.cloud_run':         { usd: 10,  basis: '1M requests, 256MB',               family: 'compute' },
  'gcp.cloud_functions':   { usd: 5,   basis: '1M invocations',                   family: 'compute' },
  'gcp.app_engine':        { usd: 30,  basis: 'F2 instance, always-on',           family: 'compute' },

  'gcp.vpc':               { usd: 0,   basis: 'free',                             family: 'network' },
  'gcp.load_balancer':     { usd: 20,  basis: 'HTTP(S) LB',                       family: 'network' },
  'gcp.cloud_cdn':         { usd: 15,  basis: '100GB cache egress',               family: 'cdn' },
  'gcp.cloud_dns':         { usd: 1,   basis: '1 zone',                           family: 'network' },

  'gcp.cloud_storage':     { usd: 5,   basis: '100GB standard',                   family: 'storage' },
  'gcp.cloud_sql':         { usd: 50,  basis: 'db-custom-1-3840, HA off',         family: 'database' },
  'gcp.bigquery':          { usd: 30,  basis: '100GB storage + 1TB query',        family: 'analytics' },
  'gcp.firestore':         { usd: 5,   basis: 'small collection, free tier+',    family: 'database' },
  'gcp.spanner':           { usd: 260, basis: '1 node regional',                  family: 'database' },
  'gcp.memorystore':       { usd: 40,  basis: 'M1 standard, 1GB',                 family: 'database' },

  'gcp.pubsub':            { usd: 2,   basis: '1M messages',                      family: 'messaging' },

  'gcp.cloud_logging':     { usd: 5,   basis: 'first 50GiB is free',              family: 'ops' },
  'gcp.cloud_monitoring':  { usd: 5,   basis: 'first 150MB free',                 family: 'ops' },
  'gcp.iam':               { usd: 0,   basis: 'free',                             family: 'security' },
  'gcp.secret_manager':    { usd: 1,   basis: '10 secrets',                       family: 'security' },

  'gcp.vertex_ai':         { usd: 60,  basis: 'small endpoint',                   family: 'ai' },

  // ── Azure ──────────────────────────────────────────────────────
  'azure.vm':              { usd: 30,  basis: 'B2s, 730h',                        family: 'compute' },
  'azure.aks':             { usd: 75,  basis: 'cluster + 2 nodes',                family: 'compute' },
  'azure.functions':       { usd: 5,   basis: '1M executions',                    family: 'compute' },
  'azure.app_service':     { usd: 55,  basis: 'B1 plan',                          family: 'compute' },
  'azure.container_apps':  { usd: 20,  basis: '2 replicas',                       family: 'compute' },

  'azure.vnet':            { usd: 0,   basis: 'free',                             family: 'network' },
  'azure.load_balancer':   { usd: 20,  basis: 'Standard',                         family: 'network' },
  'azure.application_gateway':{ usd: 35, basis: 'v2 small',                       family: 'network' },
  'azure.cdn':             { usd: 15,  basis: '100GB egress',                     family: 'cdn' },
  'azure.dns':             { usd: 1,   basis: '1 zone',                           family: 'network' },

  'azure.blob_storage':    { usd: 5,   basis: '100GB hot',                        family: 'storage' },
  'azure.sql':             { usd: 50,  basis: 'Basic 5 DTU',                      family: 'database' },
  'azure.cosmos_db':       { usd: 30,  basis: '400 RU/s provisioned',             family: 'database' },
  'azure.redis':           { usd: 50,  basis: 'Basic C1',                         family: 'database' },

  'azure.service_bus':     { usd: 10,  basis: 'Standard',                         family: 'messaging' },
  'azure.event_hubs':      { usd: 15,  basis: 'Standard 1 TU',                    family: 'messaging' },

  'azure.monitor':         { usd: 10,  basis: 'logs + metrics',                   family: 'ops' },
  'azure.key_vault':       { usd: 1,   basis: 'standard, small usage',            family: 'security' },
  'azure.ad':              { usd: 0,   basis: 'free tier',                        family: 'security' },
};

export interface NodeCost {
  nodeId: string;
  nodeLabel: string;
  nodeType: string;
  estimate: CostEntry | null;
}

export interface CostReport {
  total: number;
  currency: 'USD';
  priced: NodeCost[];
  unpriced: NodeCost[];
  byFamily: Array<{ family: CostEntry['family']; total: number }>;
}

export function estimateFor(nodeType: string): CostEntry | null {
  return TABLE[nodeType] ?? null;
}

export function summarize(
  nodes: Array<{ id: string; type: string; label: string }>,
): CostReport {
  const priced: NodeCost[] = [];
  const unpriced: NodeCost[] = [];
  const familyTotals = new Map<CostEntry['family'], number>();

  for (const n of nodes) {
    const entry = estimateFor(n.type);
    const nc: NodeCost = { nodeId: n.id, nodeLabel: n.label, nodeType: n.type, estimate: entry };
    if (entry) {
      priced.push(nc);
      familyTotals.set(entry.family, (familyTotals.get(entry.family) ?? 0) + entry.usd);
    } else {
      unpriced.push(nc);
    }
  }

  const total = priced.reduce((sum, p) => sum + (p.estimate?.usd ?? 0), 0);
  const byFamily = [...familyTotals.entries()]
    .map(([family, total]) => ({ family, total }))
    .sort((a, b) => b.total - a.total);

  return { total, currency: 'USD', priced, unpriced, byFamily };
}
